import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, Legend } from 'recharts';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';

interface DistributionData {
  name: string;
  value: number;
  color: string;
  [key: string]: unknown;
}

interface ConnectionDistributionChartProps {
  distribution: {
    zeroConnections: number;
    oneToFive: number;
    sixToTen: number;
    elevenToTwenty: number;
    twentyOnePlus: number;
  };
  title?: string;
  description?: string;
}

export function ConnectionDistributionChart({
  distribution,
  title = "Connection Distribution",
  description = "How users are connected across the network"
}: ConnectionDistributionChartProps) {
  const data: DistributionData[] = [
    { name: '0 connections', value: distribution.zeroConnections, color: '#ef4444' },
    { name: '1-5 connections', value: distribution.oneToFive, color: '#f59e0b' },
    { name: '6-10 connections', value: distribution.sixToTen, color: '#10b981' },
    { name: '11-20 connections', value: distribution.elevenToTwenty, color: '#526ac9' },
    { name: '21+ connections', value: distribution.twentyOnePlus, color: '#8b5cf6' },
  ].filter(item => item.value > 0); // Only show non-zero segments

  const total = data.reduce((sum, item) => sum + item.value, 0);

  return (
    <Card>
      <CardHeader>
        <CardTitle>{title}</CardTitle>
        <CardDescription>{description}</CardDescription>
      </CardHeader>
      <CardContent>
        <ResponsiveContainer width="100%" height={300}>
          <PieChart>
            <Pie
              data={data}
              cx="50%"
              cy="50%"
              labelLine={false}
              label={({ name, percent }) => `${name}: ${((percent as number) * 100).toFixed(0)}%`}
              outerRadius={80}
              fill="#8884d8"
              dataKey="value"
            >
              {data.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={entry.color} />
              ))}
            </Pie>
            <Tooltip
              contentStyle={{
                backgroundColor: 'hsl(var(--popover))',
                border: '1px solid hsl(var(--border))',
                borderRadius: '8px',
              }}
              formatter={(value: number) => [
                <>
                  <div className="font-semibold">{value.toLocaleString()} users</div>
                  <div className="text-xs text-muted-foreground">
                    {((value / total) * 100).toFixed(1)}% of total
                  </div>
                </>,
                'Count'
              ]}
            />
            <Legend
              verticalAlign="bottom"
              height={36}
              formatter={(value) => (
                <span className="text-xs">{value}</span>
              )}
            />
          </PieChart>
        </ResponsiveContainer>

        {/* Total count */}
        <div className="text-center mt-4">
          <p className="text-sm text-muted-foreground">
            Total Users: <span className="font-semibold">{total.toLocaleString()}</span>
          </p>
        </div>
      </CardContent>
    </Card>
  );
}
